import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const WIDTH = 800;
const HEIGHT = 400;
const MARGIN = { top: 20, right: 30, bottom: 40, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '20px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  selectLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
  },
  select: {
    padding: '6px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#1f2937',
    background: '#fff',
    cursor: 'pointer',
    outline: 'none',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  infoBox: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: 'rgba(255, 255, 255, 0.92)',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '11px',
    color: '#374151',
    lineHeight: '1.6',
    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
    pointerEvents: 'none',
  },
  infoLabel: {
    fontWeight: 600,
    color: '#6b7280',
  },
  infoValue: {
    fontWeight: 500,
    color: '#1f2937',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '2px',
  },
  tooltipLabel: {
    color: '#6b7280',
  },
  tooltipValue: {
    fontWeight: 600,
  },
  chartWrapper: {
    position: 'relative',
  },
  empty: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  oneTime: 'One-Time',
  initialServiceVisit: 'Initial Service Visit',
};

function formatFieldLabel(field) {
  return FIELD_LABELS[field] || field;
}

export default function ARIMAForecastCone({ data }) {
  const svgRef = useRef(null);
  const [selectedField, setSelectedField] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  // Determine available fields from data
  const fields = data && data.fields ? data.fields : [];
  const fieldNames = fields.map((f) => f.field);

  // Auto-select first field if none selected or selection invalid
  const activeFieldName =
    selectedField && fieldNames.includes(selectedField)
      ? selectedField
      : fieldNames[0] || null;

  const activeField = fields.find((f) => f.field === activeFieldName) || null;

  useEffect(() => {
    if (!activeField) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const innerW = WIDTH - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    const historical = activeField.historical || [];
    const forecast = activeField.forecast || [];

    if (historical.length === 0) return;

    // Parse historical dates and values
    const histParsed = historical.map((d) => ({
      date: new Date(d.date),
      value: d.value,
    }));

    // Build forecast points as dates offset from the last historical date
    const lastHistDate = histParsed[histParsed.length - 1].date;
    const lastHistValue = histParsed[histParsed.length - 1].value;

    // Estimate step interval from historical data (or default to 30 days)
    let stepInterval = 30 * 24 * 60 * 60 * 1000;
    if (histParsed.length >= 2) {
      const gaps = [];
      for (let i = 1; i < histParsed.length; i++) {
        gaps.push(histParsed[i].date.getTime() - histParsed[i - 1].date.getTime());
      }
      stepInterval = d3.median(gaps) || stepInterval;
    }

    const fcParsed = forecast.map((f) => ({
      step: f.step,
      date: new Date(lastHistDate.getTime() + f.step * stepInterval),
      value: f.value,
      lower80: f.lower80,
      upper80: f.upper80,
      lower95: f.lower95,
      upper95: f.upper95,
    }));

    // Combined x domain
    const allDates = [...histParsed.map((d) => d.date), ...fcParsed.map((d) => d.date)];

    // Combined y domain
    const allValues = [
      ...histParsed.map((d) => d.value),
      ...fcParsed.map((d) => d.value),
      ...fcParsed.map((d) => d.upper95),
      ...fcParsed.map((d) => d.lower95),
    ].filter((v) => v != null);

    const yMin = d3.min(allValues);
    const yMax = d3.max(allValues);
    const yPad = (yMax - yMin) * 0.1 || 1;

    // Scales
    const x = d3
      .scaleTime()
      .domain(d3.extent(allDates))
      .range([0, innerW]);

    const y = d3
      .scaleLinear()
      .domain([yMin - yPad, yMax + yPad])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis with date labels for historical, step labels for forecast
    const allTickDates = allDates;
    const histDateSet = new Set(histParsed.map((d) => d.date.getTime()));
    const fcStepMap = new Map(fcParsed.map((f) => [f.date.getTime(), f.step]));

    // Determine a reasonable number of ticks
    const tickCount = Math.min(allDates.length, 12);

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3
          .axisBottom(x)
          .ticks(tickCount)
          .tickSize(0)
          .tickFormat((d) => {
            const ts = d.getTime();
            // Check if this is closer to a forecast point
            const closestFc = fcParsed.reduce((best, f) => {
              const diff = Math.abs(f.date.getTime() - ts);
              return diff < best.diff ? { diff, step: f.step } : best;
            }, { diff: Infinity, step: null });

            const closestHist = histParsed.reduce((best, h) => {
              const diff = Math.abs(h.date.getTime() - ts);
              return diff < best.diff ? { diff, date: h.date } : best;
            }, { diff: Infinity, date: null });

            if (closestFc.diff < closestHist.diff && closestFc.step != null) {
              return `Step ${closestFc.step}`;
            }
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${months[d.getMonth()]} ${d.getDate()}`;
          })
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3
          .axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // 95% confidence band (lighter)
    if (fcParsed.length > 0) {
      const band95Data = [
        { date: lastHistDate, lower: lastHistValue, upper: lastHistValue },
        ...fcParsed.map((f) => ({ date: f.date, lower: f.lower95, upper: f.upper95 })),
      ];

      const areaGen = d3
        .area()
        .x((d) => x(d.date))
        .y0((d) => y(d.lower))
        .y1((d) => y(d.upper))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(band95Data)
        .attr('d', areaGen)
        .attr('fill', '#93c5fd')
        .attr('fill-opacity', 0.2)
        .attr('stroke', 'none');

      // 80% confidence band (darker)
      const band80Data = [
        { date: lastHistDate, lower: lastHistValue, upper: lastHistValue },
        ...fcParsed.map((f) => ({ date: f.date, lower: f.lower80, upper: f.upper80 })),
      ];

      g.append('path')
        .datum(band80Data)
        .attr('d', areaGen)
        .attr('fill', '#60a5fa')
        .attr('fill-opacity', 0.3)
        .attr('stroke', 'none');
    }

    // Historical line (solid blue)
    const histLineGen = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.value))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(histParsed)
      .attr('d', histLineGen)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5);

    // Historical dots
    histParsed.forEach((d) => {
      g.append('circle')
        .attr('cx', x(d.date))
        .attr('cy', y(d.value))
        .attr('r', 3)
        .attr('fill', '#2563eb');
    });

    // Forecast line (dashed) extending from last historical point
    if (fcParsed.length > 0) {
      const fcLineData = [
        { date: lastHistDate, value: lastHistValue },
        ...fcParsed.map((f) => ({ date: f.date, value: f.value })),
      ];

      const fcLineGen = d3
        .line()
        .x((d) => x(d.date))
        .y((d) => y(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(fcLineData)
        .attr('d', fcLineGen)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,3');

      // Forecast dots
      fcParsed.forEach((f) => {
        g.append('circle')
          .attr('cx', x(f.date))
          .attr('cy', y(f.value))
          .attr('r', 3)
          .attr('fill', '#2563eb')
          .attr('fill-opacity', 0.5);
      });
    }

    // Vertical forecast horizon marker
    const boundaryX = x(lastHistDate);
    g.append('line')
      .attr('x1', boundaryX)
      .attr('x2', boundaryX)
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    g.append('text')
      .attr('x', boundaryX)
      .attr('y', -6)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#6b7280')
      .text('Forecast horizon');

    // Model parameters info box (rendered in SVG, top-right corner)
    const infoG = g.append('g').attr('transform', `translate(${innerW - 160}, 4)`);

    infoG
      .append('rect')
      .attr('width', 155)
      .attr('height', 72)
      .attr('rx', 5)
      .attr('fill', 'rgba(255,255,255,0.92)')
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1);

    const params = [
      { label: '\u03C6 (phi)', value: activeField.phi },
      { label: '\u03B8 (theta)', value: activeField.theta },
      { label: '\u03C3 (sigma)', value: activeField.sigma },
      { label: 'AIC', value: activeField.aic },
    ];

    params.forEach((p, i) => {
      if (p.value == null) return;
      infoG
        .append('text')
        .attr('x', 10)
        .attr('y', 16 + i * 15)
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text(`${p.label}:`);

      infoG
        .append('text')
        .attr('x', 90)
        .attr('y', 16 + i * 15)
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', '#1f2937')
        .text(typeof p.value === 'number' ? p.value.toFixed(4) : String(p.value));
    });

    // Hover overlay for tooltip
    const crosshair = g
      .append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const allPoints = [
      ...histParsed.map((d) => ({ date: d.date, value: d.value, type: 'historical', dateStr: d.date.toISOString().slice(0, 10) })),
      ...fcParsed.map((f) => ({ date: f.date, value: f.value, type: 'forecast', step: f.step, lower80: f.lower80, upper80: f.upper80, lower95: f.lower95, upper95: f.upper95 })),
    ];

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const dateAtMouse = x.invert(mx);
        const bisect = d3.bisector((d) => d.date).left;
        let idx = bisect(allPoints, dateAtMouse);
        if (idx > 0 && idx < allPoints.length) {
          const d0 = allPoints[idx - 1].date;
          const d1 = allPoints[idx].date;
          idx = dateAtMouse - d0 > d1 - dateAtMouse ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(allPoints.length - 1, idx));

        const pt = allPoints[idx];
        const cx = x(pt.date);
        crosshair.attr('x1', cx).attr('x2', cx).style('display', null);

        if (pt.type === 'historical') {
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 10,
            type: 'historical',
            dateStr: pt.dateStr,
            value: pt.value,
          });
        } else {
          setTooltip({
            x: event.clientX + 14,
            y: event.clientY - 10,
            type: 'forecast',
            step: pt.step,
            value: pt.value,
            lower80: pt.lower80,
            upper80: pt.upper80,
            lower95: pt.lower95,
            upper95: pt.upper95,
          });
        }
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [activeField]);

  // Empty state
  if (!data || !data.fields || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>ARIMA Forecast Cone</div>
        <div style={styles.subtitle}>
          ARIMA(1,1,1) model with 80% and 95% confidence intervals
        </div>
        <div style={styles.empty}>No ARIMA forecast data available.</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>ARIMA Forecast Cone</div>
      <div style={styles.subtitle}>
        ARIMA(1,1,1) model with 80% and 95% confidence intervals
      </div>

      {/* Field selector */}
      {fieldNames.length > 1 && (
        <div style={styles.controls}>
          <span style={styles.selectLabel}>Price field:</span>
          <select
            style={styles.select}
            value={activeFieldName || ''}
            onChange={(e) => setSelectedField(e.target.value)}
          >
            {fieldNames.map((f) => (
              <option key={f} value={f}>
                {formatFieldLabel(f)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2.5" />
          </svg>
          <span>Historical</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2" strokeDasharray="6,3" />
          </svg>
          <span>Forecast</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#60a5fa" fillOpacity="0.3" rx="2" />
          </svg>
          <span>80% CI</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#93c5fd" fillOpacity="0.2" rx="2" />
          </svg>
          <span>95% CI</span>
        </div>
      </div>

      {/* Chart */}
      <div style={styles.chartWrapper}>
        <svg
          ref={svgRef}
          width="100%"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
        />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.type === 'historical'
              ? tooltip.dateStr
              : `Forecast Step ${tooltip.step}`}
          </div>
          {tooltip.type === 'historical' ? (
            <div style={styles.tooltipRow}>
              <span style={styles.tooltipLabel}>Price</span>
              <span style={{ ...styles.tooltipValue, color: '#2563eb' }}>
                ${tooltip.value != null ? tooltip.value.toFixed(2) : '--'}
              </span>
            </div>
          ) : (
            <>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>95% Upper</span>
                <span style={styles.tooltipValue}>
                  ${tooltip.upper95 != null ? tooltip.upper95.toFixed(2) : '--'}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>80% Upper</span>
                <span style={styles.tooltipValue}>
                  ${tooltip.upper80 != null ? tooltip.upper80.toFixed(2) : '--'}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={{ ...styles.tooltipLabel, color: '#2563eb', fontWeight: 600 }}>Forecast</span>
                <span style={{ ...styles.tooltipValue, color: '#2563eb' }}>
                  ${tooltip.value != null ? tooltip.value.toFixed(2) : '--'}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>80% Lower</span>
                <span style={styles.tooltipValue}>
                  ${tooltip.lower80 != null ? tooltip.lower80.toFixed(2) : '--'}
                </span>
              </div>
              <div style={styles.tooltipRow}>
                <span style={styles.tooltipLabel}>95% Lower</span>
                <span style={styles.tooltipValue}>
                  ${tooltip.lower95 != null ? tooltip.lower95.toFixed(2) : '--'}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
